import { renderCopyable } from 'components/EntityList'
import { TFunction } from 'i18next'
import { uniq } from 'lodash'
import { Participant, Person, PersonType, User } from 'model'
import { IColumn, IGroup } from 'office-ui-fabric-react'

export enum FormType {
  insert,
  update,
}

export interface FieldColumn extends IColumn {
  copyText?: boolean
  ungroupable?: ConstrainBoolean
  useFullFieldname?: boolean
  customFieldName?: string
  getGroupValue(item: any, groupColumn: IColumn): string
}

export const createFieldColumn =
  (t: TFunction) =>
  (
    fieldName: string,
    {
      minWidth,
      onRender,
      copyText,
      ungroupable,
      useFullFieldname,
      customFieldName,
      getGroupValue,
      data,
      isResizable,
      ...options
    }: Partial<FieldColumn> = {}
  ): IColumn => ({
    key: fieldName,
    fieldName,
    isResizable: true,
    name:
      customFieldName ||
      (useFullFieldname
        ? t(`field.${fieldName}.label`)
        : t(`field.${fieldName.match(/^[$_\w]+/)![0]}.label`)),
    minWidth: minWidth || 80,
    maxWidth: 1.33 * (minWidth || 80),
    onRender: copyText ? renderCopyable(onRender) : onRender,
    data: data
      ? { ...data, ungroupable, getGroupValue }
      : { ungroupable, getGroupValue },
    ...options,
  })

export const groupParticipants = (
  items: Partial<Participant>[],
  t: TFunction
) => {
  // sort items to get correct parameters for groups
  items.sort((item1, item2) =>
    getType(item1) && getType(item2)
      ? getType(item1).localeCompare(getType(item2))
      : 0
  )
  // generate set of types present in the items
  const types = uniq(
    items.map((i) => {
      if (i.person && i.person.type) {
        return i.person.type!
      } else if (i.unknownPerson) {
        return i.unknownPerson!.personType!
      } else {
        return undefined
      }
    })
  )
  // generate an IGroup for each unique type
  return types.map(
    (type: PersonType | undefined) =>
      ({
        name: t(`enum.personType.${type}`),
        startIndex: items.findIndex((item) => {
          if (item.person) {
            return item.person.type === type
          } else if (item.unknownPerson && item.unknownPerson.isUnknown) {
            return item.unknownPerson.personType === type
          }
          return false
        }),
        count: items.filter((item) => {
          if (item.person) {
            return item.person.type === type
          } else if (item.unknownPerson && item.unknownPerson.isUnknown) {
            return item.unknownPerson.personType === type
          }
          return true
        }).length,
      } as IGroup)
  )
}

export function getParticipantTypeDisplayName(
  participant: Participant,
  t: TFunction
): string {
  if (participant && participant.person) {
    return getPersonDisplayName(participant.person, t)
  } else if (participant.unknownPerson) {
    return t(`enum.personType.${participant.unknownPerson.personType}`)
  } else {
    return ''
  }
}

export function getPersonDisplayName(person: Person, t: TFunction): string {
  if (
    (person as User).extension === 'INTERN' &&
    (person as User).companyName === 'Dr. Ing. h.c. F. Porsche AG'
  ) {
    return t(`enum.personType.${(person as User).extension}`)
  } else if (
    (person as User).extension === 'INTERN' &&
    (person as User).companyName !== 'Dr. Ing. h.c. F. Porsche AG'
  ) {
    return t(`enum.personType.external`)
  } else if (
    (person as User).extension === 'ANUE' &&
    (person as User).companyName !== 'Dr. Ing. h.c. F. Porsche AG'
  ) {
    return t(`enum.personType.external`)
  } else if (
    (person as User).extension === 'TEMP' &&
    (person as User).companyName !== 'Dr. Ing. h.c. F. Porsche AG'
  ) {
    return t(`enum.personType.external`)
  } else if ((person as User).extension) {
    return t(`enum.personType.${(person as User).extension}`)
  } else if ((person as User).extension === null) {
    return t(`enum.personType.external`)
  } else {
    return t(`enum.personType.worker`)
  }
}

function getType(participant: Partial<Participant>): PersonType {
  if (participant.person) {
    return participant.person.type!
  } else if (participant.unknownPerson) {
    return participant.unknownPerson.personType!
  }
  throw new Error('No participant types defined in participant: ' + participant)
}
